import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { connectChat, joinRooms, sendMessage } from '@/api/chatSocket'
import type { ChatSocketHandlers } from '@/api/chatSocket'
import { chatService } from '@/api/services/chat'
import type { ChatMessageDto, ChatRoomDto } from '@/api/types/chat'
import { CHAT_STATUS, SENDER_TYPE } from '@/api/types/chat'
import { env } from '@/config/env'
import { useLocalStorage } from '@/composables/useLocalStorage'
import { useRequest } from '@/composables/useRequest'
import { useUserStore } from '@/stores/user'
import type { ChatMessage, ChatSummary } from '@/types/chat'
import { t } from '@/i18n'

/** Файл в очереди на отправку: превью локальное, id свой — сервер его не знает. */
export interface PendingFile {
  id: string
  url: string
  file: File
}

/** Сообщение оператора, о котором клиенту ещё не сказали, — для алерта. */
export interface ChatNotice {
  roomId: string
  messageId: string
  text: string
}

/**
 * Запасной опрос: включается, только если хаб не поднялся (нет ключа, сеть
 * режет WebSocket). Пока сокет жив, лента обновляется событиями.
 */
const POLL = 4000

/**
 * Сверка обращений в фоне. С живым сокетом она редкая и нужна на случай
 * пропущенных событий (спящая вкладка, обрыв), без сокета — это единственный
 * способ узнать, что оператор ответил.
 */
const INBOX_POLL = 20000
const INBOX_POLL_LIVE = 60000

/**
 * Время сервиса в миллисекундах. Считает он везде в UTC, но отдаёт по-разному:
 * в событии сокета время приходит с `Z`, а в истории по REST — без него. Без
 * пометки браузер читает строку как местное время, и одна и та же переписка
 * показывала часы вразнобой.
 */
function msOf(value: string): number {
  return new Date(/[Z+]|-\d{2}:\d{2}$/.test(value) ? value : `${value}Z`).getTime()
}

/** Время сообщения в ленте — часы и минуты, как в макете. */
function timeOf(value: string): string {
  return new Date(msOf(value)).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
}

/**
 * Чат поддержки. Комнаты и сообщения приходят из сервиса, а экраны работают
 * с теми же моделями, что и раньше, — вся разница в маппинге ниже.
 */
export const useChatStore = defineStore('chat', () => {
  const userStore = useUserStore()

  const roomsRequest = useRequest(chatService.rooms)
  /* Фоновая сверка молчит об ошибках: клиент её не звал, тост его бы удивил */
  const inboxRequest = useRequest(chatService.rooms, { silent: true })
  const messagesRequest = useRequest(chatService.messages, { silent: true })

  const rooms = ref<ChatRoomDto[]>([])
  const messages = ref<ChatMessageDto[]>([])
  const activeId = ref('')

  const isLoading = computed(() => roomsRequest.isLoading.value)

  /**
   * До какого момента клиент прочитал переписку — по комнатам, в миллисекундах.
   * Храним у себя: сервис считает `unreadCount` для оператора, а не для клиента
   * (в закрытом обращении ответы оператора так и лежат с `readAt: null`, а
   * счётчик там ноль), и на него опереться нельзя.
   */
  const seen = useLocalStorage<Record<string, number>>('chat-seen', {})

  /**
   * Непрочитанные ответы оператора: id сообщений по комнатам. Именно id, а не
   * счётчик, — один и тот же ответ приходит и сокетом, и фоновой сверкой,
   * и по числу его было бы не отличить от второго сообщения.
   */
  const unread = ref<Record<string, string[]>>({})

  /** Свежий ответ оператора для алерта: показываем и убираем. */
  const notice = ref<ChatNotice | null>(null)

  /** Об одном и том же сообщении не сигналим дважды за сессию. */
  const alerted = new Set<string>()

  const unreadCount = computed(() =>
    Object.values(unread.value).reduce((sum, ids) => sum + ids.length, 0),
  )
  const hasUnread = computed(() => unreadCount.value > 0)

  /**
   * Превью последнего сообщения. Одно фото без подписи сервис отдаёт с пустым
   * `lastMessageContent`, а про вложения в списке не пишет вовсе — поэтому
   * сообщение без текста подписываем сами, иначе строка выглядит пустой.
   */
  function lastOf(room: ChatRoomDto): string {
    const content = room.lastMessageContent?.trim()
    if (content) return content

    return room.lastMessageAt ? t('chat.photo') : ''
  }

  /** Заголовок обращения: номер заказа сервис отдаёт вместе с комнатой. */
  function titleOf(room: ChatRoomDto): string {
    return room.orderNumber ? `${t('orders.order')} ${room.orderNumber}` : t('chat.request')
  }

  /** Комнаты для списка: свежие сверху, как в любом мессенджере. */
  const list = computed<ChatSummary[]>(() =>
    [...rooms.value]
      .sort((a, b) => (b.lastMessageAt ?? b.createdAt).localeCompare(a.lastMessageAt ?? a.createdAt))
      .map((room) => ({
        id: room.id,
        order: titleOf(room),
        preview: lastOf(room),
        time: timeOf(room.lastMessageAt ?? room.createdAt),
        isClosed: room.status === CHAT_STATUS.closed,
        unread: Boolean(unread.value[room.id]?.length),
      })),
  )

  const activeRoom = computed(() => rooms.value.find((room) => room.id === activeId.value) ?? null)

  const isClosed = computed(() => activeRoom.value?.status === CHAT_STATUS.closed)

  /** Лента: своё сообщение — то, что отправил клиент, а не оператор. */
  const feed = computed<ChatMessage[]>(() =>
    messages.value.map((message) => ({
      id: message.id,
      text: message.content ?? '',
      time: timeOf(message.sentAt),
      own: message.senderType === SENDER_TYPE.customer,
      /* `fileUrl` относительный — файлы отдаёт сам сервис чата */
      ...(message.attachments.length
        ? { images: message.attachments.map((file) => `${env.chatUrl}${file.fileUrl}`) }
        : {}),
    })),
  )

  async function loadRooms(): Promise<void> {
    const result = await roomsRequest.execute()
    rooms.value = result?.items ?? []
  }

  async function loadMessages(roomId: string): Promise<void> {
    const result = await messagesRequest.execute(roomId)
    if (result) messages.value = result
  }

  let poll: ReturnType<typeof setInterval> | undefined

  const isLive = ref(false)

  /** Самое свежее сообщение комнаты по тому, что мы уже знаем. */
  function lastAtOf(roomId: string): number {
    const room = rooms.value.find((item) => item.id === roomId)
    const fromList = room?.lastMessageAt ? msOf(room.lastMessageAt) : 0
    const fromFeed =
      roomId === activeId.value
        ? messages.value.reduce((max, item) => Math.max(max, msOf(item.sentAt)), 0)
        : 0

    return Math.max(fromList, fromFeed)
  }

  /** Отметка «до этого места прочитано»: снимает точку и гасит алерт комнаты. */
  function markRead(roomId: string): void {
    if (!roomId) return

    const at = Math.max(lastAtOf(roomId), seen.value[roomId] ?? 0)
    seen.value = { ...seen.value, [roomId]: at }

    if (unread.value[roomId]) {
      const rest = { ...unread.value }
      delete rest[roomId]
      unread.value = rest
    }

    if (notice.value?.roomId === roomId) notice.value = null
  }

  /** Текст для алерта: у сообщения с одними вложениями его нет. */
  function previewOf(message: ChatMessageDto): string {
    return message.content?.trim() || (message.attachments.length ? t('chat.photo') : '')
  }

  /**
   * Ответы оператора, которых клиент ещё не видел: счётчик в профиле и алерт
   * о самом свежем. Приходят они и пачкой (фоновая сверка), и по одному
   * (сокет), поэтому копим id и складываем их без повторов.
   */
  function flagUnread(fresh: ChatMessageDto[]): void {
    const newest = fresh.at(-1)
    if (!newest) return

    const roomId = newest.chatRoomId
    const ids = fresh
      .filter((message) => msOf(message.sentAt) > (seen.value[roomId] ?? 0))
      .map((message) => message.id)
    if (!ids.length) return

    unread.value = {
      ...unread.value,
      [roomId]: [...new Set([...(unread.value[roomId] ?? []), ...ids])],
    }

    if (alerted.has(newest.id)) return
    alerted.add(newest.id)

    notice.value = { roomId, messageId: newest.id, text: previewOf(newest) }
  }

  function dismissNotice(): void {
    notice.value = null
  }

  /** Сообщение из хаба: своё тоже приходит обратно, поэтому чистим дубли по id. */
  function pushLive(message: ChatMessageDto): void {
    if (message.chatRoomId !== activeId.value) return
    if (messages.value.some((item) => item.id === message.id)) return

    messages.value = [...messages.value, message]
  }

  /**
   * Оператор закрыл обращение (событие `ChatClosed` с хаба уведомлений).
   * Статус ставим сразу — экран должен убрать поле ввода в тот же момент.
   *
   * Список специально не перечитываем: сервис успевает прислать событие
   * раньше, чем закрытие видно в `GET /chats`, и ответ вернул бы комнату
   * обратно в «открыта». Сверка приедет следом с `ChatUpdated`.
   */
  function markClosed(roomId: string): void {
    const id = roomId || activeId.value

    rooms.value = rooms.value.map((room) =>
      room.id === id ? { ...room, status: CHAT_STATUS.closed, closedAt: new Date().toISOString() } : room,
    )
  }

  /**
   * Живое сообщение. Открытая переписка просто дописывается и считается
   * прочитанной, всё остальное от оператора — повод сказать клиенту.
   */
  function onLiveMessage(message: ChatMessageDto): void {
    if (message.chatRoomId === activeId.value) {
      pushLive(message)
      markRead(message.chatRoomId)
      return
    }

    if (message.senderType !== SENDER_TYPE.agent) return

    flagUnread([message])
    /* Список держим свежим: с ним приедут превью, время и новые обращения */
    void loadRooms()
  }

  /* Обработчики одни на приложение: и лента, и алерт слушают те же события */
  const socketHandlers: ChatSocketHandlers = {
    message: onLiveMessage,
    updated: () => void loadRooms(),
    closed: markClosed,
  }

  /**
   * Открыли переписку: подтянули историю, пометили прочитанной, подписались
   * на комнату. Если хаб не поднялся — остаётся опрос, чтобы чат не замер.
   */
  async function openRoom(roomId: string): Promise<void> {
    activeId.value = roomId
    messages.value = []

    if (!rooms.value.length) await loadRooms()
    await loadMessages(roomId)
    void chatService.markViewed(roomId).catch(() => undefined)
    markRead(roomId)

    isLive.value = await connectChat(socketHandlers)

    clearInterval(poll)
    poll = undefined

    if (isLive.value) {
      await joinRooms([roomId])
      return
    }

    poll = setInterval(() => void loadMessages(roomId), POLL)
  }

  /**
   * Ушли с экрана переписки. Из группы комнаты не выходим: пока приложение
   * открыто, ответ оператора должен догонять клиента на любом экране.
   */
  function closeRoom(): void {
    clearPending()
    clearInterval(poll)
    poll = undefined
    markRead(activeId.value)
    activeId.value = ''
    messages.value = []
  }

  /** Ответы оператора позже отметки «прочитано» — они и есть непрочитанные. */
  async function agentMessagesAfter(roomId: string, since: number): Promise<ChatMessageDto[]> {
    const history = await chatService.messages(roomId).catch(() => null)
    if (!history) return []

    return history.filter(
      (message) => message.senderType === SENDER_TYPE.agent && msOf(message.sentAt) > since,
    )
  }

  /**
   * Сверка обращений: что в комнате появилось нового с тех пор, как клиент
   * её читал. Кто написал, список не говорит — поэтому у подозрительных
   * комнат дочитываем историю: своё же сообщение с другого устройства
   * тревожить клиента не должно.
   */
  async function syncInbox(): Promise<void> {
    const result = await inboxRequest.execute()
    if (!result) return

    rooms.value = result.items ?? []
    if (isLive.value) await joinRooms(rooms.value.map((room) => room.id))

    for (const room of rooms.value) {
      if (room.id === activeId.value) {
        markRead(room.id)
        continue
      }

      const at = room.lastMessageAt ? msOf(room.lastMessageAt) : 0
      if (!at) continue

      const since = seen.value[room.id]

      /* Комнату видим впервые: прошлую переписку не ворошим, следим с этой минуты */
      if (since === undefined) {
        seen.value = { ...seen.value, [room.id]: at }
        continue
      }

      if (at <= since) continue

      const fresh = await agentMessagesAfter(room.id, since)
      if (fresh.length) flagUnread(fresh)
      else seen.value = { ...seen.value, [room.id]: at }
    }
  }

  let inbox: ReturnType<typeof setInterval> | undefined

  /**
   * Слежение за поддержкой на всё время жизни приложения: сверяем обращения,
   * поднимаем хаб и входим в группы всех комнат клиента. Иначе `ReceiveMessage`
   * приходит только в открытую комнату, и ответ на другом экране теряется.
   */
  async function watchInbox(): Promise<void> {
    if (inbox || !userStore.userId) return

    await syncInbox()

    isLive.value = await connectChat(socketHandlers)
    if (isLive.value) await joinRooms(rooms.value.map((room) => room.id))

    inbox = setInterval(() => void syncInbox(), isLive.value ? INBOX_POLL_LIVE : INBOX_POLL)
  }

  /**
   * Отправка идёт через хаб — так сообщение сразу расходится всем участникам.
   * Без сокета остаётся REST, и тогда ответ кладём в ленту сами.
   */
  async function post(content: string, attachmentIds: string[] | null): Promise<void> {
    if (!activeId.value) return

    const sent = await sendMessage(
      activeId.value,
      userStore.userId,
      SENDER_TYPE.customer,
      content,
      attachmentIds,
    )
    if (sent) return

    const message = await chatService.send(activeId.value, {
      senderId: userStore.userId,
      senderType: SENDER_TYPE.customer,
      content,
      ...(attachmentIds ? { attachmentIds } : {}),
    })
    if (message) pushLive(message)
  }

  /** Файлы, выбранные клиентом, но ещё не отправленные. */
  const pending = ref<PendingFile[]>([])

  /** Идёт загрузка — поле ввода и кнопки на это время заблокированы. */
  const isUploading = ref(false)

  /** Превью строим локально: выбранное фото видно до того, как оно уехало. */
  function attachFiles(files: File[]): void {
    const next = files.map((file, index) => ({
      id: `${Date.now()}-${index}-${file.name}`,
      url: URL.createObjectURL(file),
      file,
    }))

    pending.value = [...pending.value, ...next]
  }

  function dropFile(id: string): void {
    const found = pending.value.find((item) => item.id === id)
    if (found) URL.revokeObjectURL(found.url)

    pending.value = pending.value.filter((item) => item.id !== id)
  }

  function clearPending(): void {
    for (const item of pending.value) URL.revokeObjectURL(item.url)
    pending.value = []
  }

  /**
   * Отправка из поля: выбранные фото уезжают вместе с текстом одним
   * сообщением — в макете это варианты «Фото» и «Фото+Текст». Пустой текст
   * сервис принимает, поэтому фото можно послать и без подписи.
   */
  async function send(text: string): Promise<void> {
    if (!activeId.value || isUploading.value) return

    const content = text.trim()
    const files = pending.value.map((item) => item.file)

    if (!files.length) {
      if (content) await post(content, null)
      return
    }

    isUploading.value = true
    try {
      const uploaded = await Promise.all(
        files.map((file) => chatService.upload(file, userStore.userId)),
      )
      await post(content, uploaded.map((file) => file.id))
      clearPending()
    } finally {
      isUploading.value = false
    }
  }

  /**
   * Обращение по заказу: если открытая комната уже есть — возвращаем её,
   * иначе заводим новую. Номер заказа обязателен вместе с id — без него
   * сервис отвечает 400.
   */
  async function openForOrder(orderId: string, orderNumber: number): Promise<string> {
    if (!rooms.value.length) await loadRooms()

    const existing = rooms.value.find(
      (room) => room.orderId === orderId && room.status === CHAT_STATUS.open,
    )
    if (existing) return existing.id

    /* Номер заказа у бэкенда числовой, но приходит и строкой — страхуемся:
       без валидного номера сервис откажет, поэтому заводим обращение без заказа */
    const withOrder = Number.isFinite(orderNumber)

    const created = await chatService.create({
      customerId: userStore.userId,
      ...(withOrder ? { orderId, orderNumber } : {}),
    })
    rooms.value = [created, ...rooms.value]

    return created.id
  }

  /** Последнее обращение — куда вести клиента из алерта без id комнаты. */
  const latestId = computed(() => list.value[0]?.id ?? '')

  return {
    rooms,
    list,
    feed,
    activeRoom,
    activeId,
    isClosed,
    isLoading,
    isLive,
    latestId,
    notice,
    unread,
    unreadCount,
    hasUnread,
    loadRooms,
    watchInbox,
    syncInbox,
    markRead,
    dismissNotice,
    openRoom,
    closeRoom,
    markClosed,
    send,
    pending,
    attachFiles,
    dropFile,
    isUploading,
    openForOrder,
  }
})
